/* === sections/cb-hero-slideshow.liquid ===
   Layer on top of the theme's <slider-component>. Flickity handles the slides
   themselves; this adds equal slide heights, deferred slide images, the live
   region, pause-on-focus, and prefers-reduced-motion behaviour. */

(function () {
  const SELECTOR = '.cb-hero__slider';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function enhance(slider) {
    if (!slider || slider.dataset.cbHeroEnhanced === 'true') return;

    const flkty = slider.flkty;
    if (!flkty) return;

    slider.dataset.cbHeroEnhanced = 'true';

    const hero = slider.closest('.cb-hero');
    const status = hero ? hero.querySelector('[data-cb-hero-status]') : null;
    const template = slider.getAttribute('data-slide-status') || 'Slide [index] of [total]';
    const autoplay = slider.getAttribute('data-cb-autoplay') === 'true';
    const total = flkty.slides ? flkty.slides.length : 0;

    function announce() {
      if (!status) return;
      status.textContent = template
        .replace('[index]', flkty.selectedIndex + 1)
        .replace('[total]', total);
    }

    flkty.on('change', announce);

    // Dots are drawn over the slide, so their colour follows it.
    function syncNavColor() {
      const slide = flkty.selectedElement;
      if (!slide || !hero) return;
      hero.classList.toggle('cb-hero--nav-light', slide.getAttribute('data-cb-nav') === 'light');
    }

    flkty.on('select', syncNavColor);
    syncNavColor();

    /*
      Slides after the first hold their image sources in data attributes
      (see snippets/cb-hero-image.liquid). Swap them in when the slide is
      coming into view, and sweep up whatever is left once the page is idle.
    */
    function loadImages(scope) {
      if (!scope) return;
      scope.querySelectorAll('img[data-cb-srcset]').forEach(function (img) {
        img.setAttribute('srcset', img.getAttribute('data-cb-srcset'));
        img.setAttribute('src', img.getAttribute('data-cb-src'));
        img.removeAttribute('data-cb-srcset');
        img.removeAttribute('data-cb-src');
      });
    }

    function loadAllImages() {
      loadImages(slider);
    }

    flkty.on('select', function () {
      loadImages(flkty.selectedElement);
      const next = flkty.cells[(flkty.selectedIndex + 1) % flkty.cells.length];
      if (next) loadImages(next.element);
    });

    slider.addEventListener('pointerdown', loadAllImages, { once: true });

    // Wait for the rest of the page to finish before picking up the slack.
    function sweepImages() {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(loadAllImages, { timeout: 3000 });
      } else {
        setTimeout(loadAllImages, 1200);
      }
    }

    if (document.readyState === 'complete') {
      sweepImages();
    } else {
      window.addEventListener('load', sweepImages);
    }

    /*
      Every slide should be the same height so the gap under the hero never
      changes as it rotates.

      The measuring is done here rather than by leaning on flickity, because
      flickity sizes its viewport from cells that already carry our height
      floor, and asking it to re-measure from inside its own resize dispatch
      hands back the pre-reset numbers. Instead the floor is dropped for the
      length of one synchronous read, the tallest slide wins, and both the CSS
      variable and flickity's viewport are set from it. Nothing is deferred, so
      the guard flag cannot be left stuck and the height cannot ratchet.
    */
    let measuring = false;

    function syncFrame() {
      if (measuring || !hero) return;
      measuring = true;

      hero.classList.add('cb-hero--measuring');

      let tallest = 0;
      slider.querySelectorAll('[data-slide]').forEach(function (slide) {
        const height = slide.getBoundingClientRect().height;
        if (height > tallest) tallest = height;
      });

      hero.classList.remove('cb-hero--measuring');

      /*
        The height settings act as a minimum. Without this the measured value
        replaced them outright, so changing either one in the theme editor did
        nothing once the script had run.
      */
      const onMobile = window.matchMedia('(max-width: 749px)').matches;
      const floor = parseFloat(
        getComputedStyle(hero).getPropertyValue(
          onMobile ? '--cb-hero-height-mobile' : '--cb-hero-height'
        )
      ) || 0;

      const frame = Math.max(Math.ceil(tallest), Math.ceil(floor));
      if (frame > 0) {
        hero.style.setProperty('--cb-hero-frame', frame + 'px');

        // Keep flickity's viewport in step, otherwise a slideshow that needs to
        // shrink holds on to the taller viewport and the gap comes back.
        const viewport = slider.querySelector('.flickity-viewport');
        if (viewport) viewport.style.height = frame + 'px';
      }

      measuring = false;
    }

    flkty.on('resize', syncFrame);
    syncFrame();

    /*
      Re-measure whenever the hero changes width. Two listeners on purpose:
      a plain resize handler, which fires even where the frame lifecycle is
      throttled, and a ResizeObserver, which also catches width changes that
      come from the page rather than the window. Both compare against the last
      width, so whichever fires first does the work and the other no-ops.
      Width only: reacting to height would loop, because syncFrame is what
      changes the height.
    */
    let lastWidth = Math.round(hero.getBoundingClientRect().width);
    let resizeTimer = 0;

    function syncOnWidthChange() {
      const width = Math.round(hero.getBoundingClientRect().width);
      if (width === lastWidth) return;
      lastWidth = width;
      syncFrame();
    }

    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(syncOnWidthChange, 150);
    });

    window.addEventListener('orientationchange', syncOnWidthChange);

    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(syncOnWidthChange).observe(hero);
    }

    // Late-loading webfonts and images change how tall a slide wants to be.
    window.addEventListener('load', syncFrame);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(syncFrame);
    }

    if (!autoplay) return;

    if (reduceMotion.matches) {
      flkty.stopPlayer();
    }

    // Pause while a keyboard user is inside the hero, resume when they leave.
    slider.addEventListener('focusin', function () {
      flkty.stopPlayer();
    });

    slider.addEventListener('focusout', function (event) {
      if (slider.contains(event.relatedTarget) || reduceMotion.matches) return;
      flkty.playPlayer();
    });

    if (typeof reduceMotion.addEventListener === 'function') {
      reduceMotion.addEventListener('change', function (event) {
        if (event.matches) {
          flkty.stopPlayer();
        } else {
          flkty.playPlayer();
        }
      });
    }
  }

  document.addEventListener('theme:slider:loaded', function (event) {
    const slider = event.detail && event.detail.slider;
    if (slider && slider.matches(SELECTOR)) enhance(slider);
  });

  // Sliders that finished initialising before this script ran.
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll(SELECTOR).forEach(enhance);
  });
})();
